import React, { useEffect, useState } from 'react';
import notificationService from 'shared/services/notificationService';
import { BrandLoaderInline } from 'features/settings/components/settingsLoader';
import { Section, Switch, rowLabel } from 'features/settings/components/SettingsRows';

const GROUPS = [
  { prefix: 'usage', label: 'Usage' },
  { prefix: 'updates', label: 'Updates' },
];

const NotificationPreferences = () => {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await notificationService.getPreferences();
      setPrefs(data.preferences);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (next) => {
    setSaving(true);
    setError('');
    try {
      const data = await notificationService.updatePreferences(next);
      setPrefs(data.preferences);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const toggle = (prefix, channel, value) => {
    if (!prefs || saving) return;
    const key = `${prefix}_${channel}`;
    const other = channel === 'in_app' ? 'email' : 'in_app';
    // At least one channel stays on.
    if (!value && !prefs[`${prefix}_${other}`]) {
      setError('Keep at least one channel on');
      return;
    }
    save({ ...prefs, [key]: value });
  };

  if (loading) return <BrandLoaderInline label="Loading notifications" />;
  if (!prefs) {
    return <div className="text-[12px] text-danger">{error || 'Unable to load preferences'}</div>;
  }

  return (
    <Section title="Notifications">
      {GROUPS.map(({ prefix, label }) => (
        <div
          key={prefix}
          className="flex flex-col gap-1 border-b border-[color:var(--border-subtle)] py-2.5 sm:flex-row sm:items-center sm:gap-4"
        >
          <div className={rowLabel}>{label}</div>
          <div className="flex items-center gap-6 text-[13px] text-[color:var(--text-primary)]">
            <label className="flex items-center gap-2">
              <Switch
                label={`${label} in app`}
                checked={!!prefs[`${prefix}_in_app`]}
                disabled={saving}
                onChange={(v) => toggle(prefix, 'in_app', v)}
              />
              App
            </label>
            <label className="flex items-center gap-2">
              <Switch
                label={`${label} by email`}
                checked={!!prefs[`${prefix}_email`]}
                disabled={saving}
                onChange={(v) => toggle(prefix, 'email', v)}
              />
              Email
            </label>
          </div>
        </div>
      ))}
      <div className="flex flex-col gap-1 border-b border-[color:var(--border-subtle)] py-2.5 sm:flex-row sm:items-center sm:gap-4">
        <div className={rowLabel}>Limits &amp; billing</div>
        <div className="text-[13px] text-[color:var(--text-muted)]">Always on</div>
      </div>
      {error ? <div className="pt-2 text-[12px] text-danger">{error}</div> : null}
    </Section>
  );
};

export default NotificationPreferences;
